import React, { useEffect, useId, useState } from "react";
import {
  Save,
  Edit3,
  Plus,
  Settings,
  X,
  Check,
  Trash2,
  List,
  Sparkles,
  ExternalLink,
  Copy,
  ChevronUp,
  ChevronDown,
  Activity,
  Wallet,
  CalendarDays,
  ArrowUpRight,
  ArrowDownRight,
  LineChart,
  PieChart,
  TriangleAlert,
  CheckCircle2,
  Loader2,
  Clock,
} from "lucide-react";
import EditableInput from "./common/EditableInput";
import Card from "./common/Card";
import SafeChart from "./common/SafeChart";
import GlowArea from "./charts/GlowArea";
import GlowDonut from "./charts/GlowDonut";
import ChartDefs from "./charts/ChartDefs";
import useChartTheme from "./charts/useChartTheme";
import { makeChartIds } from "./charts/chartTokens";
import { hasUnsavedExpenseChanges, stableStringify } from "./expenses/expenseWriteGuard";
import useUnsavedChangesGuard from "./expenses/useUnsavedChangesGuard";
import CategorizerDisclosureModal from "./expenses/CategorizerDisclosureModal";
import useCategorizerDisclosure from "./expenses/useCategorizerDisclosure";
import { CATEGORIZER_PROMPT } from "../constants/categorizerPrompt";

/* ─── AI Categorizer Prompt ──────────────────────────────────────────── */
// ARC-16: the prompt is a fixed, versioned string held verbatim in
// src/constants/categorizerPrompt.js. It carries its own category list and
// is no longer derived from the member's expense columns — the wording is
// deliberate and pinned by a SHA-256 anchor, so all four entry points below
// copy CATEGORIZER_PROMPT unmodified.

/* LLM deep-link config. Each entry tries a URL with a prefilled prompt
 * AND copies the prompt to clipboard as a universal fallback. URL prefill
 * support varies (Claude supports ?q=, ChatGPT supports ?q=, Gemini
 * does not reliably support prefill — so for Gemini we just open the
 * app and rely on clipboard paste). */
const LLM_TARGETS = {
  claude: {
    name: "Claude",
    accent: "#D97706", // amber/coral
    url: (prompt) =>
      `https://claude.ai/new?q=${encodeURIComponent(prompt)}`,
    fallbackUrl: "https://claude.ai/new",
  },
  chatgpt: {
    name: "ChatGPT",
    accent: "#10A37F", // openai green
    url: (prompt) =>
      `https://chatgpt.com/?q=${encodeURIComponent(prompt)}`,
    fallbackUrl: "https://chatgpt.com/",
  },
  gemini: {
    name: "Gemini",
    accent: "#4285F4", // google blue
    // Gemini does not reliably support URL-prefilled prompts. Just open
    // the app and rely on clipboard paste.
    url: () => "https://gemini.google.com/app",
    fallbackUrl: "https://gemini.google.com/app",
  },
};

// Shared v4 section-label recipe (spec §4 "Section label"): quiet uppercase caption.
const LABEL = "text-[11px] font-semibold tracking-[0.04em] uppercase text-arc-ink-4";

/**
 * ExpensesTab – editable expense table with flexible month insertion
 *
 * Line items: each (month, category) cell can drill into a list of individual
 * purchases (e.g., "Costco $245", "Whole Foods $189"). When line items exist
 * for a cell, the cell value is auto-computed as the sum of items.
 */

const genItemId = () =>
  `i_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

// Sum line items for a (row, field)
const sumItems = (row, field) => {
  const items = row?.items?.[field];
  if (!Array.isArray(items) || items.length === 0) return null;
  return items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
};

// Effective value: line-item sum overrides typed-in value when items exist
const cellValue = (row, field) => {
  const itemSum = sumItems(row, field);
  return itemSum != null ? itemSum : row?.[field] || 0;
};

const ExpensesTab = ({
  expenseData,
  expenseColumns = [
    "rent",
    "utilities",
    "insurance",
    "groceries",
    "entertainment",
    "travel",
    "other",
  ],
  editMode,
  onToggleEdit,
  updateExpenseData,
  addNextMonth,
  onUpdateColumns,
  setExpenseData,
  /* ─── ARC-14: committed (server-confirmed) state + explicit save ──────
     All optional. Without them the tab behaves exactly as it did before —
     but the shell (FinancialDashboard) supplies them, and only then can the
     derived widgets tell "saved" apart from "typed". */
  committedExpenseData,
  committedExpenseColumns,
  onSaveExpenses,      // async () => { success, error } — performs a real save
  hasUnsavedChanges,   // optional override of the local dirty check
}) => {
  const [showColumnEditor, setShowColumnEditor] = useState(false);
  const [showMonthInsertModal, setShowMonthInsertModal] = useState(false);
  const [tempColumns, setTempColumns] = useState([]);
  const [newColumnName, setNewColumnName] = useState("");

  // Line-items drill-down modal: { rowIdx, field } when open, null when closed
  const [itemsModal, setItemsModal] = useState(null);
  const [draftDescription, setDraftDescription] = useState("");
  const [draftAmount, setDraftAmount] = useState("");

  // AI categorizer toast: { kind: "success"|"error", text }
  const [aiToast, setAiToast] = useState(null);

  /* ─── ARC-14: committed state, save outcome, unsaved-changes guard ─────
     The table is the edit buffer. Everything derived from it below — the three
     stat badges and both charts — reads the last SERVER-CONFIRMED state
     instead, so a member is never shown a figure that was not persisted.
     Falls back to the buffer when the shell passes no committed snapshot,
     which is the pre-ARC-14 behaviour. */
  const hasCommitted = Array.isArray(committedExpenseData);
  const committedData = hasCommitted ? committedExpenseData : expenseData;
  const committedColumns =
    Array.isArray(committedExpenseColumns) && committedExpenseColumns.length
      ? committedExpenseColumns
      : expenseColumns;

  const dirty =
    typeof hasUnsavedChanges === "boolean"
      ? hasUnsavedChanges
      : hasCommitted &&
        hasUnsavedExpenseChanges(
          { expenseColumns, expenseData },
          { expenseColumns: committedColumns, expenseData: committedExpenseData }
        );

  // { kind: "saving" | "success" | "error", text, signature }
  const [saveStatus, setSaveStatus] = useState(null);
  const saving = saveStatus?.kind === "saving";
  // A "saved" notice describes the buffer as it was when it was written, so it
  // stands while the buffer is unchanged since that write OR the buffer matches
  // the committed state (the save may have merged in another session's edits).
  // Once the member edits again it is no longer true of what is on screen.
  const bufferSignature = stableStringify({ expenseColumns, expenseData });
  const savedNoticeStillTrue =
    !dirty || saveStatus?.signature === bufferSignature;
  const visibleStatus =
    saveStatus?.kind === "success" && !savedNoticeStillTrue ? null : saveStatus;

  useUnsavedChangesGuard(dirty);

  const handleEditSaveClick = async () => {
    if (!editMode) {
      setSaveStatus(null);
      if (onToggleEdit) onToggleEdit();
      return;
    }
    if (saving) return;

    // No save handler wired — keep the old toggle, and claim nothing was saved.
    if (typeof onSaveExpenses !== "function") {
      if (onToggleEdit) onToggleEdit();
      return;
    }

    setSaveStatus({ kind: "saving", text: "Saving your changes…" });

    let result;
    try {
      result = await onSaveExpenses();
    } catch (e) {
      result = { success: false, error: e?.message };
    }

    if (result && result.success) {
      setSaveStatus({
        kind: "success",
        text: "Changes saved. The figures below come from your saved data.",
        signature: stableStringify({ expenseColumns, expenseData }),
      });
      if (onToggleEdit) onToggleEdit();
      return;
    }

    // Failed: stay in edit mode so every entered value stays on screen and editable.
    /* Several upstream save errors (offline, unconfirmed, write failed) already
       tell the member their entries survived and to try again. Appending our own
       reassurance to those printed that promise twice in one alert. Add it only
       when the error does not already make it. */
    const reassures = (msg) => /still here|still on screen/i.test(msg);
    setSaveStatus({
      kind: "error",
      text: result?.error
        ? reassures(result.error)
          ? `Not saved: ${result.error}`
          : `Not saved: ${result.error} Your entries are still here — try Save again.`
        : "Not saved. Your entries are still here — check your connection and try Save again.",
    });
  };

  /* ─── Glow-chart theme (spec: docs/CHART_STYLE.md) ───────────────────
     Tab signature glow = VIOLET. The shared tone system exposes no violet key,
     so we use its closest NEON violet/purple tone (`magenta` → --chart-magenta),
     resolved via useChartTheme so it re-themes and dials the glow back in light.
     chartIds gives this instance its own namespaced arcGlow filter for the
     stat-badge glow (the donut/area own their own via makeChartIds internally). */
  const chartTheme = useChartTheme();
  const chartIds = makeChartIds(useId());
  const violet = chartTheme.tone?.magenta || "#E879F9";

  // Defer Recharts until after mount (avoids a 0-width first measure).
  const [chartsReady, setChartsReady] = useState(false);
  useEffect(() => {
    setChartsReady(true);
  }, []);

  /* ─── AI Categorizer launch handler ─────────────────────────────── */
  // 1. Copies the categorizer prompt to clipboard (universal fallback).
  // 2. Opens the LLM in a new tab with prompt prefilled where supported.
  // 3. Shows a small toast confirming the copy.
  const launchLLM = async (key) => {
    const target = LLM_TARGETS[key];
    if (!target) return;

    let copied = false;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(CATEGORIZER_PROMPT);
        copied = true;
      }
    } catch (_) {
      copied = false;
    }

    // Open the LLM. Try URL prefill first; fall back to bare URL on errors.
    let urlToOpen;
    try {
      urlToOpen = target.url(CATEGORIZER_PROMPT);
      // Some browsers cap URL length; if our encoded URL is huge, fall back.
      if (urlToOpen.length > 7500) urlToOpen = target.fallbackUrl;
    } catch (_) {
      urlToOpen = target.fallbackUrl;
    }

    window.open(urlToOpen, "_blank", "noopener,noreferrer");

    setAiToast({
      kind: "success",
      text: copied
        ? `Prompt copied. Paste it into ${target.name} and attach your statement.`
        : `Couldn't auto-copy. Use the "Copy prompt" button, then paste into ${target.name}.`,
    });

    // Auto-dismiss after 5 seconds
    setTimeout(() => setAiToast(null), 5000);
  };

  const copyPromptOnly = async () => {
    try {
      await navigator.clipboard.writeText(CATEGORIZER_PROMPT);
      setAiToast({
        kind: "success",
        text: "Prompt copied. Paste it into any AI of your choice.",
      });
    } catch (_) {
      setAiToast({
        kind: "error",
        text: "Couldn't copy. Select the prompt manually below.",
      });
    }
    setTimeout(() => setAiToast(null), 5000);
  };

  /* ─── ARC-15: disclosure gate ───────────────────────────────────────
     All four entry points below run through `disclosure.run(...)`. With a
     current acknowledgement on record it calls straight through; without one
     the action is held until the member acknowledges, so nothing is copied and
     no tab is opened before then. */
  const disclosure = useCategorizerDisclosure();
  const gated = (action) => () => disclosure.run(action);

  // For month insertion
  const [newMonthName, setNewMonthName] = useState("");
  const [insertPosition, setInsertPosition] = useState("end");
  const [selectedMonthIndex, setSelectedMonthIndex] = useState(0);

  /* ─── Line-item helpers ─────────────────────────────────────────── */
  const getItemsFor = (rowIdx, field) =>
    expenseData[rowIdx]?.items?.[field] || [];

  const writeItems = (rowIdx, field, newItems) => {
    setExpenseData((curr) => {
      const next = [...curr];
      const row = { ...next[rowIdx] };
      const items = { ...(row.items || {}) };
      if (newItems.length === 0) {
        delete items[field];
      } else {
        items[field] = newItems;
      }
      row.items = items;
      // Sync the numeric column to the new sum so the rest of the app sees it
      const sum = newItems.reduce(
        (s, it) => s + (parseFloat(it.amount) || 0),
        0
      );
      row[field] = sum;
      // Recompute total across all columns
      row.total = expenseColumns.reduce(
        (s, col) => s + (cellValue({ ...row, items: { ...items } }, col) || 0),
        0
      );
      next[rowIdx] = row;
      return next;
    });
  };

  const addLineItem = (rowIdx, field, description, amount) => {
    const cur = getItemsFor(rowIdx, field);
    const item = {
      id: genItemId(),
      description: description.trim() || "Item",
      amount: parseFloat(amount) || 0,
      date: new Date().toISOString().slice(0, 10),
    };
    writeItems(rowIdx, field, [...cur, item]);
  };

  const removeLineItem = (rowIdx, field, itemId) => {
    const cur = getItemsFor(rowIdx, field);
    writeItems(
      rowIdx,
      field,
      cur.filter((it) => it.id !== itemId)
    );
  };

  const updateLineItem = (rowIdx, field, itemId, updates) => {
    const cur = getItemsFor(rowIdx, field);
    writeItems(
      rowIdx,
      field,
      cur.map((it) => (it.id === itemId ? { ...it, ...updates } : it))
    );
  };

  const handleAddDraft = () => {
    if (!itemsModal) return;
    if (!draftAmount || parseFloat(draftAmount) <= 0) return;
    addLineItem(itemsModal.rowIdx, itemsModal.field, draftDescription, draftAmount);
    setDraftDescription("");
    setDraftAmount("");
  };

  /* ─── Helpers ────────────────────────────────────────────────────────── */
  const formatColumnName = (columnName) =>
    columnName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const formatColumnKey = (displayName) =>
    displayName
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "");

  /* ─── Column Editor Logic ────────────────────────────────────────────── */
  const openColumnEditor = () => {
    setTempColumns([...expenseColumns]);
    setShowColumnEditor(true);
  };

  const handleAddColumn = () => {
    if (!newColumnName.trim()) return;
    const columnKey = formatColumnKey(newColumnName.trim());
    if (tempColumns.includes(columnKey)) {
      alert("Column already exists!");
      return;
    }
    setTempColumns([...tempColumns, columnKey]);
    setNewColumnName("");
  };

  const handleRemoveColumn = (columnToRemove) =>
    setTempColumns(tempColumns.filter((col) => col !== columnToRemove));

  const handleSaveColumns = () => {
    if (onUpdateColumns) onUpdateColumns(tempColumns);
    setShowColumnEditor(false);
  };

  const handleCancelColumns = () => {
    setTempColumns([]);
    setNewColumnName("");
    setShowColumnEditor(false);
  };

  const moveColumn = (index, direction) => {
    const newColumns = [...tempColumns];
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < newColumns.length) {
      [newColumns[index], newColumns[newIndex]] = [
        newColumns[newIndex],
        newColumns[index],
      ];
      setTempColumns(newColumns);
    }
  };

  /* ─── Month Insertion Logic ──────────────────────────────────────────── */
  const openMonthInsertModal = () => {
    setNewMonthName("");
    setInsertPosition("end");
    setSelectedMonthIndex(0);
    setShowMonthInsertModal(true);
  };

  const handleInsertMonth = () => {
    if (!newMonthName.trim()) {
      alert("Please enter a month name");
      return;
    }

    // Create new month object with all columns initialized to 0
    const newMonth = { month: newMonthName.trim(), total: 0 };
    expenseColumns.forEach((col) => {
      newMonth[col] = 0;
    });

    const updatedData = [...expenseData];

    if (insertPosition === "beginning") {
      updatedData.unshift(newMonth);
    } else if (insertPosition === "end") {
      updatedData.push(newMonth);
    } else if (insertPosition === "before") {
      updatedData.splice(selectedMonthIndex, 0, newMonth);
    } else if (insertPosition === "after") {
      updatedData.splice(selectedMonthIndex + 1, 0, newMonth);
    }

    setExpenseData(updatedData);
    setShowMonthInsertModal(false);
  };

  /* ─── Delete Month Handler ───────────────────────────────────────────── */
  const deleteMonth = (monthIndex) => {
    if (window.confirm("Are you sure you want to delete this month?")) {
      const updated = expenseData.filter((_, idx) => idx !== monthIndex);
      setExpenseData(updated);
    }
  };

  /* ─── Derived analytics ───────────────────────────────────────────────
     ARC-14: committed state ONLY. These never read the edit buffer. */
  const months = committedData.length;
  const totalAll = committedData.reduce((s, r) => s + (r.total || 0), 0);
  const avg = months > 0 ? totalAll / months : 0;
  const latestRow = committedData[months - 1];
  const latestTotal = latestRow?.total || 0;
  const prevTotal = months > 1 ? committedData[months - 2]?.total || 0 : null;
  const momDelta = prevTotal != null ? latestTotal - prevTotal : null;
  const momPct = prevTotal ? (momDelta / prevTotal) * 100 : null;
  const fmtUSD = (n) =>
    `$${(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

  // Category totals across every tracked month → the donut's NEON categorical set.
  const categoryTotals = committedColumns
    .map((col) => ({
      name: formatColumnName(col),
      amount: committedData.reduce((s, row) => s + (cellValue(row, col) || 0), 0),
    }))
    .filter((c) => c.amount > 0)
    .sort((a, b) => b.amount - a.amount);

  /* ─── Render ─────────────────────────────────────────────────────────── */
  return (
    <div className="space-y-6">
      {/* Hidden defs so the stat-badge icons can carry the shared arcGlow filter. */}
      <ChartDefs ids={chartIds} colors={chartTheme} />

      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <p className={`${LABEL} mb-1`}>Cash Flow</p>
          <h3 className="text-h2 text-arc-ink">Expense Tracking</h3>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={openColumnEditor}
            className="flex items-center gap-2 bg-arc-card text-arc-ink font-semibold rounded-arc-ctl px-4 py-2.5 ring-1 ring-arc-line shadow-arc-sm transition hover:bg-arc-paper-2"
          >
            <Settings size={16} /> Columns
          </button>

          <button
            onClick={openMonthInsertModal}
            className="flex items-center gap-2 bg-arc-accent text-white font-semibold rounded-arc-ctl px-4 py-2.5 shadow-arc-sm transition hover:brightness-105 active:brightness-95"
          >
            <Plus size={16} /> Add Month
          </button>

          <button
            onClick={handleEditSaveClick}
            disabled={saving}
            aria-busy={saving}
            className={`flex items-center gap-2 font-semibold rounded-arc-ctl px-4 py-2.5 shadow-arc-sm transition disabled:opacity-60 disabled:cursor-not-allowed ${
              editMode
                ? "bg-arc-pos text-white hover:brightness-105 active:brightness-95"
                : "bg-arc-card text-arc-ink ring-1 ring-arc-line hover:bg-arc-paper-2"
            }`}
          >
            {saving ? (
              <Loader2 size={16} className="animate-spin" />
            ) : editMode ? (
              <Save size={16} />
            ) : (
              <Edit3 size={16} />
            )}{" "}
            {saving ? "Saving…" : editMode ? "Save" : "Edit"}
          </button>
        </div>
      </div>

      {/* ─── ARC-14: explicit save outcome ─────────────────────────────────
          Leaving edit mode is not confirmation. Every save resolves here to a
          visible saving / saved / not-saved state. On failure the tab stays in
          edit mode and the entered values stay in the buffer. */}
      {visibleStatus && (
        <div
          role={visibleStatus.kind === "error" ? "alert" : "status"}
          aria-live={visibleStatus.kind === "error" ? "assertive" : "polite"}
          className={`flex gap-3 p-4 rounded-arc-card shadow-arc-sm ring-1 ${
            visibleStatus.kind === "error"
              ? "bg-arc-neg-soft ring-arc-neg-soft text-arc-neg"
              : visibleStatus.kind === "success"
              ? "bg-arc-pos-soft ring-arc-pos-soft text-arc-pos"
              : "bg-arc-paper-2 ring-arc-line text-arc-ink-2"
          }`}
        >
          {visibleStatus.kind === "error" ? (
            <TriangleAlert size={18} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
          ) : visibleStatus.kind === "success" ? (
            <CheckCircle2 size={18} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
          ) : (
            <Loader2 size={18} className="flex-shrink-0 mt-0.5 animate-spin" aria-hidden="true" />
          )}
          <div className="text-[14.5px] leading-[1.55]">{visibleStatus.text}</div>
        </div>
      )}

      {/* Month Insert Modal */}
      {showMonthInsertModal && (
        <div className="fixed inset-0 bg-[rgba(0,0,0,.6)] backdrop-blur-[5px] flex items-center justify-center z-50 p-4">
          <div className="bg-arc-card rounded-arc-card ring-1 ring-arc-line shadow-arc-lg p-[22px] max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-h2 text-arc-ink">Insert New Month</h4>
              <button
                onClick={() => setShowMonthInsertModal(false)}
                aria-label="Close"
                className="text-arc-ink-3 hover:text-arc-ink transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Month Name Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-arc-ink-2 mb-2">
                Month Name (e.g., "Jan 2025", "February 2025")
              </label>
              <input
                type="text"
                value={newMonthName}
                onChange={(e) => setNewMonthName(e.target.value)}
                placeholder="Enter month name..."
                className="w-full px-3 py-2 bg-arc-paper-2 text-arc-ink placeholder-arc-ink-4 ring-1 ring-arc-line rounded-arc-ctl focus:outline-none focus:ring-2 focus:ring-arc-accent"
                autoFocus
              />
            </div>

            {/* Position Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-arc-ink-2 mb-2">
                Insert Position
              </label>
              <select
                value={insertPosition}
                onChange={(e) => setInsertPosition(e.target.value)}
                className="w-full px-3 py-2 bg-arc-paper-2 text-arc-ink ring-1 ring-arc-line rounded-arc-ctl focus:outline-none focus:ring-2 focus:ring-arc-accent"
              >
                <option value="beginning">At the beginning</option>
                <option value="end">At the end</option>
                {expenseData.length > 0 && (
                  <>
                    <option value="before">Before a specific month</option>
                    <option value="after">After a specific month</option>
                  </>
                )}
              </select>
            </div>

            {/* Specific Month Selection (if before/after) */}
            {(insertPosition === "before" || insertPosition === "after") && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-arc-ink-2 mb-2">
                  Select Month
                </label>
                <select
                  value={selectedMonthIndex}
                  onChange={(e) => setSelectedMonthIndex(parseInt(e.target.value))}
                  className="w-full px-3 py-2 bg-arc-paper-2 text-arc-ink ring-1 ring-arc-line rounded-arc-ctl focus:outline-none focus:ring-2 focus:ring-arc-accent"
                >
                  {expenseData.map((row, idx) => (
                    <option key={idx} value={idx}>
                      {row.month}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowMonthInsertModal(false)}
                className="px-4 py-2 bg-arc-card text-arc-ink font-semibold ring-1 ring-arc-line rounded-arc-ctl transition hover:bg-arc-paper-2"
              >
                Cancel
              </button>
              <button
                onClick={handleInsertMonth}
                className="px-4 py-2 bg-arc-pos text-white font-semibold rounded-arc-ctl transition hover:brightness-105 active:brightness-95 flex items-center gap-2"
              >
                <Check size={16} /> Insert Month
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Column Editor Modal */}
      {showColumnEditor && (
        <div className="fixed inset-0 bg-[rgba(0,0,0,.6)] backdrop-blur-[5px] flex items-center justify-center z-50 p-4">
          <div className="bg-arc-card rounded-arc-card ring-1 ring-arc-line shadow-arc-lg p-[22px] max-w-md w-full max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-h2 text-arc-ink">Customize Expense Columns</h4>
              <button
                onClick={handleCancelColumns}
                aria-label="Close"
                className="text-arc-ink-3 hover:text-arc-ink transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Add New Column */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-arc-ink-2 mb-2">
                Add New Column
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newColumnName}
                  onChange={(e) => setNewColumnName(e.target.value)}
                  placeholder="Column name"
                  className="flex-1 px-3 py-2 bg-arc-paper-2 text-arc-ink placeholder-arc-ink-4 ring-1 ring-arc-line rounded-arc-ctl focus:outline-none focus:ring-2 focus:ring-arc-accent"
                  onKeyPress={(e) => e.key === "Enter" && handleAddColumn()}
                />
                <button
                  onClick={handleAddColumn}
                  aria-label="Add column"
                  className="px-3 py-2 bg-arc-accent text-white rounded-arc-ctl transition hover:brightness-105 active:brightness-95"
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>

            {/* Current Columns */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-arc-ink-2 mb-2">
                Current Columns
              </label>
              <div className="space-y-2">
                {tempColumns.map((column, index) => (
                  <div
                    key={column}
                    className="flex items-center gap-2 p-2 bg-arc-paper-2 ring-1 ring-arc-line rounded-arc-sm"
                  >
                    <span className="flex-1 text-arc-ink">{formatColumnName(column)}</span>
                    <div className="flex gap-1">
                      <button
                        onClick={() => moveColumn(index, "up")}
                        disabled={index === 0}
                        aria-label="Move up"
                        className="p-1.5 text-arc-ink-2 bg-arc-card ring-1 ring-arc-line rounded-arc-sm transition hover:bg-arc-paper disabled:opacity-40"
                      >
                        <ChevronUp size={14} />
                      </button>
                      <button
                        onClick={() => moveColumn(index, "down")}
                        disabled={index === tempColumns.length - 1}
                        aria-label="Move down"
                        className="p-1.5 text-arc-ink-2 bg-arc-card ring-1 ring-arc-line rounded-arc-sm transition hover:bg-arc-paper disabled:opacity-40"
                      >
                        <ChevronDown size={14} />
                      </button>
                      <button
                        onClick={() => handleRemoveColumn(column)}
                        aria-label="Remove column"
                        className="p-1.5 text-arc-neg bg-arc-neg-soft rounded-arc-sm transition hover:brightness-105"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-2">
              <button
                onClick={handleCancelColumns}
                className="px-4 py-2 bg-arc-card text-arc-ink font-semibold ring-1 ring-arc-line rounded-arc-ctl transition hover:bg-arc-paper-2"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveColumns}
                className="px-4 py-2 bg-arc-pos text-white font-semibold rounded-arc-ctl transition hover:brightness-105 active:brightness-95 flex items-center gap-2"
              >
                <Check size={16} /> Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── AI Statement Categorizer ──────────────────────────────── */}
      <Card
        hover={false}
        className="bg-gradient-to-br from-arc-accent-soft via-transparent to-transparent"
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-arc-ctl bg-arc-accent-soft ring-1 ring-arc-line flex items-center justify-center flex-shrink-0">
            <Sparkles size={18} className="text-arc-accent-ink" />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`${LABEL} mb-1`}>AI Statement Categorizer</p>
            <h4 className="font-semibold text-arc-ink mb-1.5">
              Drop your bank statement into an AI — paste totals back here
            </h4>
            <p className="text-sm text-arc-ink-2 leading-relaxed">
              Click an AI below. The categorizer prompt copies to your clipboard
              and the chat opens in a new tab. Paste the prompt, attach your
              bank or credit-card statement (CSV, PDF, or screenshot), and the
              AI returns clean category totals you can type into the table.
            </p>
          </div>
        </div>

        {/* LLM buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Claude */}
          <button
            onClick={gated(() => launchLLM("claude"))}
            className="group flex items-center gap-3 px-4 py-3 rounded-arc-ctl ring-1 ring-arc-line bg-arc-paper-2 hover:bg-arc-paper hover:ring-arc-line-2 transition-all"
            title="Open Claude with the categorizer prompt"
          >
            {/* Claude logo (Anthropic asterisk-style mark) */}
            <span
              className="w-9 h-9 rounded-arc-sm flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: "#D97706" }}
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="white" aria-hidden="true">
                <path d="M12 2 L13.2 8.8 L19.2 5.4 L15.8 11.4 L22.6 12.6 L15.8 13.8 L19.2 19.8 L13.2 16.4 L12 23.2 L10.8 16.4 L4.8 19.8 L8.2 13.8 L1.4 12.6 L8.2 11.4 L4.8 5.4 L10.8 8.8 Z" />
              </svg>
            </span>
            <div className="flex-1 text-left min-w-0">
              <p className="font-semibold text-sm text-arc-ink">Claude</p>
              <p className="text-[11px] text-arc-ink-4">Best with PDFs & docs</p>
            </div>
            <ExternalLink
              size={14}
              className="text-arc-ink-4 group-hover:text-arc-accent-ink flex-shrink-0"
            />
          </button>

          {/* ChatGPT */}
          <button
            onClick={gated(() => launchLLM("chatgpt"))}
            className="group flex items-center gap-3 px-4 py-3 rounded-arc-ctl ring-1 ring-arc-line bg-arc-paper-2 hover:bg-arc-paper hover:ring-arc-line-2 transition-all"
            title="Open ChatGPT with the categorizer prompt"
          >
            {/* OpenAI logo (six-petal mark) */}
            <span
              className="w-9 h-9 rounded-arc-sm flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: "#10A37F" }}
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="white" aria-hidden="true">
                <path d="M22.28 9.82a5.96 5.96 0 0 0-.51-4.91 6.05 6.05 0 0 0-6.51-2.9A6.07 6.07 0 0 0 4.99 4.07a5.96 5.96 0 0 0-3.99 2.9 6.05 6.05 0 0 0 .74 7.09 5.97 5.97 0 0 0 .51 4.91 6.06 6.06 0 0 0 6.52 2.91A5.99 5.99 0 0 0 13.27 24a6.06 6.06 0 0 0 5.77-4.21 5.98 5.98 0 0 0 3.99-2.9 6.06 6.06 0 0 0-.75-7.07Zm-9 12.59a4.49 4.49 0 0 1-2.88-1.04l.14-.08 4.78-2.76a.78.78 0 0 0 .39-.68v-6.74L17.74 12c.02 0 .04.02.04.05v5.58a4.5 4.5 0 0 1-4.5 4.78ZM3.66 18.31a4.48 4.48 0 0 1-.54-3.02l.14.08 4.78 2.76a.77.77 0 0 0 .78 0l5.84-3.37v2.34a.07.07 0 0 1-.03.06L9.8 19.95a4.5 4.5 0 0 1-6.14-1.64ZM2.5 8.94a4.48 4.48 0 0 1 2.34-1.97v5.69a.78.78 0 0 0 .39.68l5.81 3.35-2.02 1.17a.07.07 0 0 1-.07 0L4.11 15.07a4.5 4.5 0 0 1-1.61-6.13Zm16.51 3.83-5.84-3.39 2.02-1.16a.07.07 0 0 1 .07 0l4.83 2.79a4.5 4.5 0 0 1-.68 8.1v-5.66a.79.79 0 0 0-.4-.68Zm2.01-3.02-.14-.09-4.77-2.78a.77.77 0 0 0-.78 0L9.49 10.25V7.91a.07.07 0 0 1 .03-.06l4.83-2.78a4.5 4.5 0 0 1 6.67 4.66Zm-12.64 4.15-2.02-1.17a.07.07 0 0 1-.04-.05V7.13a4.5 4.5 0 0 1 7.38-3.45l-.14.08L8.78 6.52a.77.77 0 0 0-.39.68Zm1.1-2.36 2.6-1.5 2.6 1.5v3l-2.6 1.5-2.6-1.5Z" />
              </svg>
            </span>
            <div className="flex-1 text-left min-w-0">
              <p className="font-semibold text-sm text-arc-ink">ChatGPT</p>
              <p className="text-[11px] text-arc-ink-4">Fast & widely available</p>
            </div>
            <ExternalLink
              size={14}
              className="text-arc-ink-4 group-hover:text-arc-accent-ink flex-shrink-0"
            />
          </button>

          {/* Gemini */}
          <button
            onClick={gated(() => launchLLM("gemini"))}
            className="group flex items-center gap-3 px-4 py-3 rounded-arc-ctl ring-1 ring-arc-line bg-arc-paper-2 hover:bg-arc-paper hover:ring-arc-line-2 transition-all"
            title="Open Gemini with the categorizer prompt"
          >
            {/* Gemini logo (4-point star) */}
            <span className="w-9 h-9 rounded-arc-sm flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-[#4285F4] to-[#9B72CB]">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="white" aria-hidden="true">
                <path d="M12 2 C12 7.5 16.5 12 22 12 C16.5 12 12 16.5 12 22 C12 16.5 7.5 12 2 12 C7.5 12 12 7.5 12 2 Z" />
              </svg>
            </span>
            <div className="flex-1 text-left min-w-0">
              <p className="font-semibold text-sm text-arc-ink">Gemini</p>
              <p className="text-[11px] text-arc-ink-4">Free with Google account</p>
            </div>
            <ExternalLink
              size={14}
              className="text-arc-ink-4 group-hover:text-arc-accent-ink flex-shrink-0"
            />
          </button>
        </div>

        {/* Copy-only fallback + toast */}
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <button
              onClick={gated(copyPromptOnly)}
              className="flex items-center gap-2 text-xs text-arc-ink-2 hover:text-arc-accent-ink transition-colors"
            >
              <Copy size={12} />
              Copy prompt only
            </button>

            {/* ARC-15: read-only reopen, shown once the member has acknowledged */}
            {disclosure.acknowledged && (
              <button
                type="button"
                onClick={disclosure.openNotice}
                className="text-xs text-arc-ink-3 hover:text-arc-accent-ink underline underline-offset-2 transition-colors"
              >
                Data &amp; privacy notice
              </button>
            )}
          </div>

          {aiToast && (
            <div
              className={`text-xs px-3 py-1.5 rounded-arc-ctl ring-1 ${
                aiToast.kind === "success"
                  ? "ring-arc-accent-soft bg-arc-accent-soft text-arc-accent-ink"
                  : "ring-arc-neg-soft bg-arc-neg-soft text-arc-neg"
              }`}
            >
              {aiToast.text}
            </div>
          )}
        </div>
      </Card>

      {/* ARC-15: disclosure gate for the categorizer */}
      <CategorizerDisclosureModal
        open={!!disclosure.modal}
        mode={disclosure.modal?.mode}
        onCancel={disclosure.cancel}
        onAccept={disclosure.accept}
        saving={disclosure.saving}
        error={disclosure.error}
      />

      {/* Editable Table */}
      <Card hover={false}>
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto">
            <thead>
              <tr>
                <th className={`${LABEL} px-3 py-2 text-left border-b border-arc-line-2`}>
                  Month
                </th>
                {expenseColumns.map((column) => (
                  <th
                    key={column}
                    className={`${LABEL} px-3 py-2 text-right border-b border-arc-line-2`}
                  >
                    {formatColumnName(column)}
                  </th>
                ))}
                <th className={`${LABEL} px-3 py-2 text-right border-b border-arc-line-2`}>
                  Total
                </th>
                <th className={`${LABEL} px-3 py-2 text-center border-b border-arc-line-2`}>
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {expenseData.map((row, idx) => (
                <tr
                  key={`${row.month}-${idx}`}
                  className="border-b border-arc-line transition-colors hover:bg-arc-paper"
                >
                  <td className="px-3 py-2.5 font-medium text-arc-ink">{row.month}</td>

                  {expenseColumns.map((field) => {
                    const itemSum = sumItems(row, field);
                    const hasItems = itemSum != null;
                    const display = hasItems ? itemSum : row[field] || 0;
                    const itemCount = (row?.items?.[field] || []).length;

                    return (
                      <td key={field} className="px-3 py-2.5 text-right text-arc-ink-2">
                        <div className="flex items-center justify-end gap-1">
                          {editMode ? (
                            hasItems ? (
                              <span
                                className="tnum text-arc-accent-ink text-sm w-20 text-right"
                                title={`${itemCount} line item${itemCount === 1 ? "" : "s"}`}
                              >
                                {itemSum.toLocaleString()}
                              </span>
                            ) : (
                              <EditableInput
                                value={row[field] || 0}
                                onChange={(val) => updateExpenseData(idx, field, val)}
                                className="w-20 text-right"
                              />
                            )
                          ) : (
                            <span className="tnum">
                              {hasItems ? (
                                <span className="text-arc-accent-ink">{display.toLocaleString()}</span>
                              ) : (
                                display.toLocaleString()
                              )}
                            </span>
                          )}
                          {editMode && (
                            <button
                              onClick={() => setItemsModal({ rowIdx: idx, field })}
                              className="p-1 rounded-arc-sm text-arc-ink-3 hover:text-arc-accent-ink relative transition-colors"
                              title={hasItems ? `${itemCount} line items — click to edit` : "Add line items"}
                            >
                              <List size={14} />
                              {hasItems && (
                                <span className="absolute -top-1 -right-1 text-[9px] tnum px-1 rounded-full bg-arc-accent text-arc-paper">
                                  {itemCount}
                                </span>
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    );
                  })}

                  <td className="px-3 py-2.5 text-right font-semibold tnum text-arc-neg">
                    ${row.total.toLocaleString()}
                  </td>

                  {/* Delete button per month */}
                  <td className="px-3 py-2.5 text-center">
                    <button
                      onClick={() => deleteMonth(idx)}
                      className="text-arc-ink-3 hover:text-arc-neg transition-colors"
                      title="Delete Month"
                      aria-label="Delete month"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Analytics: signature glow stat badges + charts.
          ARC-14: these render from committed (saved) state only. */}
      {committedData.length > 0 && (
        <>
          {/* Pending-edits marker: says plainly that the figures below are the
              saved ones, so nothing here can be mistaken for a confirmed save. */}
          {dirty && (
            <div
              role="status"
              className="flex items-center gap-2 px-3 py-2 rounded-arc-ctl ring-1 ring-arc-line bg-arc-paper-2 text-arc-ink-2 text-[12.5px]"
            >
              <Clock size={14} className="flex-shrink-0 text-arc-ink-3" aria-hidden="true" />
              <span>
                Unsaved edits in the table. The figures and charts below show your last
                saved data until you save.
              </span>
            </div>
          )}

          {/* Summary stat badges — tab-accent violet icon carries the arcGlow filter */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-arc-card ring-1 ring-arc-line bg-arc-card shadow-arc-sm p-[22px]">
              <div className="flex items-center gap-2 mb-2">
                <span
                  className="inline-flex items-center justify-center w-8 h-8"
                  style={{ color: violet }}
                >
                  <Activity size={18} style={{ filter: `url(#${chartIds.glow})` }} />
                </span>
                <p className={LABEL}>Avg Monthly Expense</p>
              </div>
              <p
                className="text-[28px] font-bold tracking-[-0.02em] leading-none tnum"
                style={{ color: violet }}
              >
                {fmtUSD(avg)}
              </p>
              <p className="text-[11.5px] text-arc-ink-3 mt-1.5">
                across {months} month{months === 1 ? "" : "s"} tracked
              </p>
            </div>

            <div className="rounded-arc-card ring-1 ring-arc-line bg-arc-card shadow-arc-sm p-[22px]">
              <div className="flex items-center gap-2 mb-2">
                <span
                  className="inline-flex items-center justify-center w-8 h-8"
                  style={{ color: violet }}
                >
                  <Wallet size={18} style={{ filter: `url(#${chartIds.glow})` }} />
                </span>
                <p className={LABEL}>Total Expenses</p>
              </div>
              <p className="text-[28px] font-bold tracking-[-0.02em] leading-none tnum text-arc-ink">
                {fmtUSD(totalAll)}
              </p>
              <p className="text-[11.5px] text-arc-ink-3 mt-1.5">all months combined</p>
            </div>

            <div className="rounded-arc-card ring-1 ring-arc-line bg-arc-card shadow-arc-sm p-[22px]">
              <div className="flex items-center gap-2 mb-2">
                <span
                  className="inline-flex items-center justify-center w-8 h-8"
                  style={{ color: violet }}
                >
                  <CalendarDays size={18} style={{ filter: `url(#${chartIds.glow})` }} />
                </span>
                <p className={LABEL}>Latest Month</p>
              </div>
              <p className="text-[28px] font-bold tracking-[-0.02em] leading-none tnum text-arc-ink">
                {fmtUSD(latestTotal)}
              </p>
              <div className="flex items-center gap-2 mt-1.5">
                <p className="text-[11.5px] text-arc-ink-3">{latestRow?.month || "—"}</p>
                {momDelta != null && momDelta !== 0 && (
                  <span
                    className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-arc-sm text-[11px] font-semibold ${
                      momDelta > 0
                        ? "text-arc-neg bg-arc-neg-soft"
                        : "text-arc-pos bg-arc-pos-soft"
                    }`}
                    title="Change vs previous month"
                  >
                    {momDelta > 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                    <span className="tnum">
                      {momPct != null ? `${Math.abs(momPct).toFixed(1)}%` : fmtUSD(Math.abs(momDelta))}
                    </span>
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Charts row: monthly trend (violet GlowArea) + category mix (NEON GlowDonut) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card hover={false}>
              <div className="flex items-center gap-2 mb-4">
                <LineChart size={16} style={{ color: violet }} />
                <h3 className="text-h2 text-arc-ink">Monthly Expense Trend</h3>
              </div>
              <SafeChart chartsReady={chartsReady}>
                <GlowArea
                  data={committedData}
                  xKey="month"
                  series={[{ key: "total", name: "Total Expenses", tone: "magenta" }]}
                  yTickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  valueFormatter={(v) => `$${Number(v).toLocaleString()}`}
                />
              </SafeChart>
            </Card>

            <Card hover={false}>
              <div className="flex items-center gap-2 mb-4">
                <PieChart size={16} style={{ color: violet }} />
                <h3 className="text-h2 text-arc-ink">Spending by Category</h3>
              </div>
              {categoryTotals.length > 0 ? (
                <>
                  <SafeChart chartsReady={chartsReady}>
                    <GlowDonut
                      data={categoryTotals}
                      dataKey="amount"
                      nameKey="name"
                      valueFormatter={(v) => `$${Number(v).toLocaleString()}`}
                    />
                  </SafeChart>
                  <div className="mt-4 text-center">
                    <p className="text-sm text-arc-ink-3">
                      Total tracked:{" "}
                      <span className="font-semibold tnum text-arc-accent-ink">
                        {fmtUSD(totalAll)}
                      </span>
                    </p>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center h-[300px]">
                  <p className="text-sm text-arc-ink-3">
                    Add category totals to see your spending mix.
                  </p>
                </div>
              )}
            </Card>
          </div>
        </>
      )}

      {/* Column Info */}
      <div className="text-sm text-arc-ink-2">
        <p>
          <strong className="text-arc-ink">Current columns:</strong>{" "}
          {expenseColumns.map((col) => formatColumnName(col)).join(", ")}
        </p>
        <p className="mt-1 text-arc-ink-3">
          Click "Columns" to add, remove, or reorder expense categories. Click the
          list icon next to any cell to drill into individual line items (e.g.,
          "Costco $245, Whole Foods $189") that auto-sum into the cell total.
        </p>
      </div>

      {/* Line items drill-down modal */}
      {itemsModal && (() => {
        const row = expenseData[itemsModal.rowIdx];
        const field = itemsModal.field;
        const items = getItemsFor(itemsModal.rowIdx, field);
        const total = items.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0);
        const fmt = (n) => `$${(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-[rgba(0,0,0,.6)] backdrop-blur-[5px]"
              onClick={() => setItemsModal(null)}
            />
            <div className="relative bg-arc-card rounded-arc-card ring-1 ring-arc-line shadow-arc-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6">
              <div className="flex items-start justify-between mb-5">
                <div>
                  <p className={`${LABEL} mb-1`}>Line items</p>
                  <h3 className="text-h2 text-arc-ink">
                    {formatColumnName(field)} — {row?.month}
                  </h3>
                  <p className="text-arc-ink-3 text-sm mt-1">
                    Add each transaction below. The cell total auto-sums.
                  </p>
                </div>
                <button
                  onClick={() => setItemsModal(null)}
                  aria-label="Close"
                  className="p-2 rounded-arc-ctl text-arc-ink-3 hover:text-arc-ink hover:bg-arc-paper-2 transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Add new item */}
              <div className="rounded-arc-ctl ring-1 ring-arc-line bg-arc-paper-2 p-4 mb-5">
                <p className={`${LABEL} mb-3`}>Add transaction</p>
                <div className="flex flex-wrap gap-3">
                  <input
                    type="text"
                    value={draftDescription}
                    onChange={(e) => setDraftDescription(e.target.value)}
                    placeholder="e.g., Costco, Whole Foods, Trader Joe's"
                    className="flex-1 min-w-[200px] px-3 py-2 rounded-arc-ctl text-sm bg-arc-card text-arc-ink placeholder-arc-ink-4 ring-1 ring-arc-line focus:outline-none focus:ring-2 focus:ring-arc-accent"
                  />
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-arc-ink-4 text-sm">
                      $
                    </span>
                    <input
                      type="number"
                      step={0.01}
                      value={draftAmount}
                      onChange={(e) => setDraftAmount(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleAddDraft();
                      }}
                      placeholder="0.00"
                      className="w-32 pl-7 pr-3 py-2 rounded-arc-ctl text-sm tnum text-right bg-arc-card text-arc-ink placeholder-arc-ink-4 ring-1 ring-arc-line focus:outline-none focus:ring-2 focus:ring-arc-accent"
                    />
                  </div>
                  <button
                    onClick={handleAddDraft}
                    disabled={!draftAmount || parseFloat(draftAmount) <= 0}
                    className="bg-arc-accent text-white px-4 py-2 rounded-arc-ctl text-sm font-semibold flex items-center gap-2 transition hover:brightness-105 active:brightness-95 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Plus size={14} strokeWidth={2.5} />
                    Add
                  </button>
                </div>
              </div>

              {/* Existing items */}
              {items.length === 0 ? (
                <div className="rounded-arc-ctl border border-dashed border-arc-line bg-arc-paper-2 p-8 text-center">
                  <p className="text-arc-ink-3 text-sm">
                    No line items yet. Add your first transaction above.
                  </p>
                </div>
              ) : (
                <div className="space-y-2 mb-5">
                  {items.map((it) => (
                    <div
                      key={it.id}
                      className="flex items-center gap-3 rounded-arc-ctl ring-1 ring-arc-line bg-arc-paper-2 p-3"
                    >
                      <input
                        type="text"
                        value={it.description}
                        onChange={(e) =>
                          updateLineItem(itemsModal.rowIdx, field, it.id, {
                            description: e.target.value,
                          })
                        }
                        className="flex-1 min-w-0 px-3 py-1.5 rounded-arc-ctl text-sm bg-transparent text-arc-ink border border-transparent hover:border-arc-line focus:outline-none focus:border-arc-line"
                      />
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-arc-ink-4 text-sm">
                          $
                        </span>
                        <input
                          type="number"
                          step={0.01}
                          value={it.amount}
                          onChange={(e) =>
                            updateLineItem(itemsModal.rowIdx, field, it.id, {
                              amount: parseFloat(e.target.value) || 0,
                            })
                          }
                          className="w-28 pl-7 pr-3 py-1.5 rounded-arc-ctl text-sm tnum text-right bg-arc-card text-arc-ink ring-1 ring-arc-line focus:outline-none focus:ring-2 focus:ring-arc-accent"
                        />
                      </div>
                      <button
                        onClick={() => removeLineItem(itemsModal.rowIdx, field, it.id)}
                        aria-label="Remove item"
                        className="p-1.5 rounded-arc-ctl text-arc-ink-3 hover:text-arc-neg hover:bg-arc-neg-soft transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Footer total + close */}
              <div className="flex items-center justify-between pt-4 border-t border-arc-line">
                <div>
                  <p className={`${LABEL} mb-1`}>Category total</p>
                  <p className="text-2xl tnum font-bold text-arc-accent-ink">{fmt(total)}</p>
                  <p className="text-xs text-arc-ink-4 mt-0.5">
                    {items.length} item{items.length === 1 ? "" : "s"}
                  </p>
                </div>
                <button
                  onClick={() => setItemsModal(null)}
                  className="bg-arc-accent text-white px-5 py-2.5 rounded-arc-ctl text-sm font-semibold flex items-center gap-2 transition hover:brightness-105 active:brightness-95"
                >
                  <Check size={14} strokeWidth={2.5} />
                  Done
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default ExpensesTab;
